import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactElement,
  Children,
} from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import type {
  GroupBase,
  GroupProps as ReactSelectGroupProps,
  MenuListProps as ReactSelectMenuListProps,
  OptionProps as ReactSelectOptionProps,
} from 'react-select';

import { NoOptionsMessage, noResultsMessage } from './NoOptionsMessage.js';
import type { OptionProps } from './Option.js';
import {
  applyStoredSelectionSortingOptions,
  sortSelectedOptions,
} from './sort-selected-options-first.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { screenReaderOnly } from '../../../styles/screen-reader-only.sty.js';
import * as styles from '../styles/Select.sty.js';
import type { BaseGroup, BaseOption } from '../types.js';

/**
 * @internal
 */
export interface MenuListFunctionRefObject {
  /** Function to scroll to the last option. */
  scrollToBottom: () => void;
  /** Function to scroll to a specific virtualizer index. */
  scrollToIndex: (index: number) => void;
}

type MenuListProps<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
> = ReactSelectMenuListProps<OptionValue, isMulti, Group>;

/**
 * Counts non-disabled leaf options across both flat and grouped children.
 * @param children - The current rendered option children.
 * @returns The number of selectable options.
 */
function countSelectableOptions(children: ReactElement[]): number {
  let count = 0;
  for (const child of children) {
    if (child === null || child === undefined) {
      continue;
    }
    const props = (
      child as unknown as ReactElement<
        | ReactSelectGroupProps<BaseOption<unknown>>
        | ReactSelectOptionProps<BaseOption<unknown>>
      >
    ).props;
    // Distinguish between OptionProps and GroupProps: OptionProps has type:'option', GroupProps does not.
    // Flat options expose isDisabled directly, groups have it in the options array.
    if ('type' in props) {
      if (!props.isDisabled) {
        count += 1;
      }
    } else {
      // props.options is the post-filter list; data.options would be the unfiltered original
      count += props.options.filter((o) => !o.isDisabled).length;
    }
  }
  return count;
}

/**
 * Builds the screen-reader live-region text shown when a custom filter is present.
 * @param intl - The intl instance used to format messages.
 * @param children - The current rendered option children.
 * @param isFiltering - Whether a non-empty filter is active.
 * @param isShowingNoOptions - Whether the no-options message is currently displayed.
 * @returns The live-region text to announce.
 */
function buildFilterAnnouncement(
  intl: IntlShape,
  children: ReactElement[],
  isFiltering: boolean,
  isShowingNoOptions: boolean,
): string {
  if (!isFiltering) {
    if (isShowingNoOptions) {
      return '';
    }
    return intl.formatMessage(
      {
        defaultMessage:
          '{count, plural, one {Showing # option} other {Showing all # options}}',
        id: 'k21q5kyTS9rGFWLF',
        description:
          'Screen reader announcement when the select filter is cleared, showing the total number of available options.',
      },
      { count: countSelectableOptions(children) },
    );
  }

  if (isShowingNoOptions) {
    return intl.formatMessage(noResultsMessage);
  }

  return intl.formatMessage(
    {
      defaultMessage:
        '{count, plural, one {# option found} other {# options found}}',
      id: 'IUvOgujQzBX+gSUq',
      description:
        'Screen reader announcement of the number of options matching the select filter.',
    },
    { count: countSelectableOptions(children) },
  );
}

export function MenuList<
  OptionValue,
  isMulti extends boolean,
  Group extends BaseGroup<OptionValue>,
>(props: Readonly<MenuListProps<BaseOption<OptionValue>, isMulti, Group>>) {
  const { children, selectProps, innerProps } = props;

  const parentRef = useRef<HTMLDivElement | null>(null);
  // Get a setter on the parentRef to get notified when the element re-renders.
  const [notifyParentRefChange, setNotifyParentRefChange] =
    useState<HTMLDivElement | null>();

  const initialRender = useRef(true);
  const sortingMap = useRef(new WeakMap());

  let childrenArray = Children.toArray(children) as Array<
    ReactElement<OptionProps<BaseOption<OptionValue>, isMulti, Group>>
  >;

  const currentChildrenLength = useRef(childrenArray.length);

  const isShowingNoOptions =
    childrenArray.length === 1 && childrenArray[0].type === NoOptionsMessage;

  // During the initial rendering, the sorting is created and positions saved in a WeakMap.
  // If only the NoOptionsMessage/empty state is shown we don't need any sorting
  if (selectProps.showSelectedOptionsFirst && !isShowingNoOptions) {
    if (initialRender.current === true) {
      [childrenArray, sortingMap.current] = sortSelectedOptions(childrenArray);
    } else if (currentChildrenLength.current !== childrenArray.length) {
      currentChildrenLength.current = childrenArray.length;
      [childrenArray, sortingMap.current] = sortSelectedOptions(childrenArray);
    }
    // Otherwise the data is sorted by the initial sorting parameters.
    else {
      const [newChildrenArray, newSortingMap] =
        applyStoredSelectionSortingOptions(childrenArray, sortingMap.current);
      childrenArray = newChildrenArray;
      if (newSortingMap) {
        sortingMap.current = newSortingMap;
      }
    }
  }

  initialRender.current = false;

  const intl = useIntl();
  const isFiltering =
    selectProps.customFilter !== undefined &&
    selectProps.inputValue !== undefined &&
    selectProps.inputValue !== '';

  const filterAnnouncement = buildFilterAnnouncement(
    intl,
    childrenArray,
    isFiltering,
    isShowingNoOptions,
  );

  const optionVirtualizer = useVirtualizer({
    count: childrenArray.length,
    getScrollElement: () => parentRef.current,
    overscan: 5,
    estimateSize: () => 32,
  });

  const menulistref = useMergeRefs([
    props.innerRef,
    parentRef,
    setNotifyParentRefChange,
    selectProps.contentRef,
  ]);

  useImperativeHandle(
    selectProps.menuListFunctionRef,
    (): MenuListFunctionRefObject => ({
      scrollToBottom: () => {
        optionVirtualizer.scrollToIndex(childrenArray.length);
      },
      scrollToIndex: (index: number) => {
        optionVirtualizer.scrollToIndex(index);
      },
    }),
  );

  // When the parentElement re-renders, we will need to remeasure the virtualizer in order to
  // keep the correct height.
  useEffect(() => {
    requestAnimationFrame(() => {
      optionVirtualizer.elementsCache.forEach((element) => {
        optionVirtualizer.measureElement(element);
      });
    });
    // Deliberately leave optionVirtualizer out of the update deps to prevent a maximum update depth exceeded issue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifyParentRefChange, childrenArray.length]);

  return (
    <>
      {selectProps.customFilter && (
        // Rendered as a sibling, not a child, of the listbox below — role="listbox"
        // only permits option/group children, so a nested role="status" trips axe's
        // aria-required-children rule.
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className={screenReaderOnly}
        >
          {filterAnnouncement}
        </div>
      )}
      <div
        {...innerProps}
        ref={menulistref}
        className={clsx(styles.selectMenuListWrapper, 'strato-select-menulist')}
        style={{
          width:
            optionVirtualizer.scrollOffset !== null &&
            optionVirtualizer.scrollOffset > 0 &&
            selectProps.contentWidth === 'max-content'
              ? optionVirtualizer.scrollRect?.width
              : undefined,
        }}
      >
        {/* role="presentation" on a div is intentional — neutralizes the virtualization wrapper so screen readers count options correctly */}
        <div
          role="presentation"
          style={{
            height: optionVirtualizer.getTotalSize(),
            display: 'grid',
            // Array.from is used instead of .map() because measurementsCache can be sparse, causing gaps between options.
            gridTemplateRows: Array.from(
              optionVirtualizer.measurementsCache,
              (el) => `${el?.size ?? 32}px`,
            ).join(' '),
          }}
          key={`wrapper-${selectProps.options.length}`}
          className={styles.selectVirtualizationWrapper}
        >
          {optionVirtualizer.getVirtualItems().map((virtualRow) => {
            const child = childrenArray?.[virtualRow.index];
            return (
              <div
                key={virtualRow.key}
                className="strato-select-option"
                // data-index is required for the virtualizer to map the measure
                // element correctly to the measured index.
                data-index={virtualRow.index}
                style={{
                  gridRow: virtualRow.index + 1,
                  minWidth: 0,
                  height: 'fit-content',
                }}
                ref={(el) => {
                  optionVirtualizer.measureElement(el);
                }}
              >
                {child}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
